import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
// Turn Version chantier Lot A — bouton "Version chantier" par devis
// (reprend le composant utilisé par QuoteEditorPage, aucun refactor).
import { ExportExecutionBtn } from "@/components/quote/QuoteActionButtons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  ChevronLeft,
  FileText,
  Plus,
  Hammer,
  CalendarClock,
  Wallet,
  User as UserIcon,
} from "lucide-react";
import ChantierInvoicesSection from "@/components/invoice/ChantierInvoicesSection";
import ChantierWorkflowSection from "@/components/chantier/ChantierWorkflowSection";
import ChantierSupplierCostsSection from "@/components/chantier/ChantierSupplierCostsSection";
import ChantierMarginBlock from "@/components/chantier/ChantierMarginBlock";
import ChantierLaborAndSubcontractingCard from "@/components/chantier/ChantierLaborAndSubcontractingCard";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";

const CHANTIER_STATUSES = [
  ["en_attente_rdv", "En attente RDV"],
  ["devise", "Devisé"],
  ["a_planifier", "À planifier"],
  ["en_cours", "En cours"],
  ["finalise", "Finalisé"],
  ["termine", "Terminé"],
];

const QUOTE_STATUS_LABELS = {
  brouillon: { label: "Brouillon", cls: "badge-neutral" },
  envoye: { label: "Envoyé", cls: "badge-warning" },
  signe: { label: "Signé", cls: "badge-success" },
  refuse: { label: "Refusé", cls: "badge-error" },
  expire: { label: "Expiré", cls: "badge-neutral" },
};

function fmtEur(n) {
  return new Intl.NumberFormat("fr-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));
}

// Format court fr-BE pour la fiche chantier backoffice : 05/09/2026
function fmtDateShortBE(isoDate) {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
}

// Nombre de jours entre aujourd'hui (UTC 00h00) et la date ISO YYYY-MM-DD.
// Retourne un entier signé : négatif si passée, 0 si aujourd'hui, positif si à venir.
function daysUntil(isoDate) {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [y, m, d] = isoDate.split("-").map((s) => parseInt(s, 10));
  if (!y || !m || !d) return null;
  const target = new Date(y, m - 1, d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

export default function ChantierPage() {
  const { chantierId } = useParams();
  const navigate = useNavigate();
  const { state } = useAuth();
  const user = state?.user;
  const [chantier, setChantier] = useState(null);
  const [lead, setLead] = useState(null);
  const [types, setTypes] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  // Phase 9bis-hub-proactif : édition locale de expected_start_date + prompt notify client
  const [dateDraft, setDateDraft] = useState("");
  const [notifyPrompt, setNotifyPrompt] = useState(null); // { newDate, oldDate, isFirstSet }
  const [notifyChecked, setNotifyChecked] = useState(true);
  const [savingDate, setSavingDate] = useState(false);

  const load = async () => {
    try {
      const [chRes, tyRes, qRes] = await Promise.all([
        api.get(`/chantiers/${chantierId}`),
        api.get("/chantier-types").catch(() => ({ data: [] })),
        api.get(`/quotes?chantier_id=${chantierId}&include_amendments=true`),
      ]);
      setChantier(chRes.data);
      setDateDraft(chRes.data?.expected_start_date || "");
      setTypes(tyRes.data || []);
      setQuotes(qRes.data.items || []);
      const leadRes = await api.get(`/leads/${chRes.data.lead_id}`);
      setLead(leadRes.data.lead);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, [chantierId]);

  const patchChantier = async (updates) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/chantiers/${chantierId}`, updates);
      setChantier(data);
      toast.success("Chantier mis à jour");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Phase 9bis-hub-proactif : validation + prompt "notify client"
  const onDateSaveClick = () => {
    const newDate = (dateDraft || "").trim() || null;
    const oldDate = chantier.expected_start_date || null;
    if (newDate === oldDate) {
      toast.info("Aucun changement de date");
      return;
    }
    if (newDate && !/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
      toast.error("Format de date invalide.");
      return;
    }
    setNotifyChecked(true);
    setNotifyPrompt({ newDate, oldDate, isFirstSet: !oldDate });
  };

  const confirmDateSave = async () => {
    if (!notifyPrompt) return;
    setSavingDate(true);
    try {
      const body = {
        expected_start_date: notifyPrompt.newDate,
        notify_client: notifyChecked,
      };
      const { data } = await api.patch(`/chantiers/${chantierId}`, body);
      setChantier(data);
      setDateDraft(data.expected_start_date || "");
      setNotifyPrompt(null);
      toast.success(
        notifyChecked
          ? "Date enregistrée et client prévenu."
          : "Date enregistrée."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDate(false);
    }
  };

  const daysToStart = useMemo(
    () => daysUntil(chantier?.expected_start_date),
    [chantier?.expected_start_date]
  );
  const isUrgentSoon =
    typeof daysToStart === "number" && daysToStart >= 0 && daysToStart < 7;
  const isPast =
    typeof daysToStart === "number" && daysToStart < 0;

  const newQuote = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/quotes", {
        chantier_id: chantierId,
        lines: [],
        notes: "",
      });
      toast.success("Brouillon de devis créé");
      navigate(`/quotes/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  if (!chantier) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const currentType = types.find((t) => t.id === chantier.chantier_type_id);

  return (
    <div className="space-y-6" data-testid="chantier-page">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          data-testid="chantier-back-btn"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="inline-flex items-center gap-2 text-2xl font-bold">
          <Hammer className="h-5 w-5 text-[#C4553B]" /> {chantier.name}
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="border-slate-800 bg-slate-900/40 lg:col-span-2">
          <CardContent className="space-y-4 p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Nom du chantier</Label>
                <Input
                  value={chantier.name}
                  onChange={(e) => setChantier({ ...chantier, name: e.target.value })}
                  onBlur={() =>
                    chantier.name?.trim() && patchChantier({ name: chantier.name.trim() })
                  }
                  className="border-slate-700 bg-slate-950/40 text-slate-100"
                  data-testid="chantier-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Statut</Label>
                <Select
                  value={chantier.status}
                  onValueChange={(v) => patchChantier({ status: v })}
                >
                  <SelectTrigger
                    className="border-slate-700 bg-slate-950/40 text-slate-100"
                    data-testid="chantier-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    {CHANTIER_STATUSES.map(([v, l]) => (
                      <SelectItem key={v} value={v}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-slate-300">Type de chantier</Label>
                <Select
                  value={chantier.chantier_type_id || "__none__"}
                  onValueChange={(v) =>
                    patchChantier({ chantier_type_id: v === "__none__" ? null : v })
                  }
                >
                  <SelectTrigger
                    className="border-slate-700 bg-slate-950/40 text-slate-100"
                    data-testid="chantier-type-select"
                  >
                    <SelectValue placeholder="Aucun type" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900">
                    <SelectItem value="__none__">— Aucun —</SelectItem>
                    {types.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`chantier-type-option-${t.id}`}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentType?.checklist_items?.length > 0 && (
                  <p className="text-xs text-slate-500">
                    {currentType.checklist_items.length} élément(s) de checklist définis pour ce type.
                  </p>
                )}
              </div>

              {/* Phase 9bis-hub-proactif — Date prévisionnelle de démarrage */}
              <div className="space-y-1.5 sm:col-span-2" data-testid="chantier-start-date-block">
                <Label className="inline-flex items-center gap-2 text-slate-300">
                  <CalendarClock className="h-3.5 w-3.5 text-[#C4553B]" />
                  Date prévue de démarrage
                </Label>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Input
                    type="date"
                    value={dateDraft || ""}
                    onChange={(e) => setDateDraft(e.target.value)}
                    className="border-slate-700 bg-slate-950/40 text-slate-100 sm:max-w-[220px]"
                    data-testid="chantier-start-date-input"
                  />
                  <Button
                    onClick={onDateSaveClick}
                    disabled={saving || savingDate}
                    className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                    data-testid="chantier-start-date-save-btn"
                  >
                    {chantier.expected_start_date ? "Modifier" : "Enregistrer"}
                  </Button>
                  {chantier.expected_start_date && (
                    <span className="text-sm text-slate-400" data-testid="chantier-start-date-current">
                      Actuellement : <b className="text-slate-100">{fmtDateShortBE(chantier.expected_start_date)}</b>
                    </span>
                  )}
                  {isUrgentSoon && (
                    <Badge
                      className="border border-[#D99A2B]/60 bg-[#D99A2B]/15 text-[#F0C572]"
                      data-testid="chantier-start-date-urgent-badge"
                    >
                      Dans {daysToStart} j
                    </Badge>
                  )}
                  {isPast && (
                    <Badge
                      className="border border-slate-600/60 bg-slate-800/40 text-slate-400"
                      data-testid="chantier-start-date-past-badge"
                    >
                      Passée
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Une modification proposera d&apos;envoyer un email d&apos;information au client (option cochée par défaut).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/40">
          <CardContent className="space-y-2 p-5">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">
              <UserIcon className="h-4 w-4" /> Client
            </div>
            {lead ? (
              <div className="space-y-1 text-sm text-slate-300">
                <div className="font-medium text-slate-100">
                  {lead.first_name} {lead.last_name}
                </div>
                {lead.email && <div className="text-slate-400">{lead.email}</div>}
                {lead.phone && <div className="text-slate-400">{lead.phone}</div>}
                {(lead.address_street || lead.address_city) && (
                  <div className="text-xs text-slate-500">
                    {[lead.address_street, lead.address_zip, lead.address_city]
                      .filter(Boolean)
                      .join(" ")}
                  </div>
                )}
                <Link
                  to={`/leads/${lead.id}`}
                  className="mt-2 inline-block text-xs text-[#E9A98F] hover:underline"
                  data-testid="chantier-lead-link"
                >
                  Voir la fiche lead →
                </Link>
                {/* Phase 10b — KPI compact "Marge sur achats" (Admin only, invisible sinon)
                    Lot Accès commercial — masqué pour role=commercial (double sécurité :
                    backend renvoie 403, mais on n'appelle même pas le endpoint). */}
                {!hasRole(user, ["commercial"]) && (
                  <div className="mt-4">
                    <ChantierMarginBlock chantierId={chantier.id} variant="compact" />
                  </div>
                )}
              </div>
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-800 bg-slate-900/40" data-testid="chantier-quotes-section">
        <CardContent className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">
              <FileText className="h-4 w-4" /> Devis ({quotes.length})
            </h3>
            <Button
              onClick={newQuote}
              disabled={creating}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="new-quote-btn"
            >
              {creating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-1 h-4 w-4" />
              )}
              Nouveau devis
            </Button>
          </div>
          {quotes.length === 0 ? (
            <p className="text-sm text-slate-500">Aucun devis pour ce chantier.</p>
          ) : (
            <ul className="space-y-2">
              {/* Phase Avenants-α-1 (2026-09-10) — Groupement : avenants sous
                  leur parent (indentation + label "Avenant A{n}"). Un avenant
                  orphelin (parent absent) ne s'affiche pas seul. */}
              {(() => {
                const parents = quotes.filter((q) => !q.is_amendment);
                const amendmentsByParent = quotes.reduce((acc, q) => {
                  if (q.is_amendment && q.parent_quote_id) {
                    (acc[q.parent_quote_id] = acc[q.parent_quote_id] || []).push(q);
                  }
                  return acc;
                }, {});
                const rows = [];
                parents.forEach((q) => {
                  rows.push({ q, indent: 0 });
                  (amendmentsByParent[q.id] || [])
                    .sort((a, b) => (a.number || "").localeCompare(b.number || ""))
                    .forEach((a) => rows.push({ q: a, indent: 1 }));
                });
                return rows.map(({ q, indent }) => {
                const meta = QUOTE_STATUS_LABELS[q.status] || QUOTE_STATUS_LABELS.brouillon;
                return (
                  <li
                    key={q.id}
                    data-testid={q.is_amendment ? `chantier-amendment-row-${q.id}` : `chantier-quote-item-${q.id}`}
                    className={`flex items-center gap-2 rounded border border-slate-800 bg-slate-950/30 p-3 hover:border-[#C4553B]/50 hover:bg-slate-950/50 ${indent === 1 ? "ml-8 border-l-2 border-l-[#C4553B]/50" : ""}`}
                  >
                    <Link
                      to={`/quotes/${q.id}`}
                      className="flex min-w-0 flex-1 items-center justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {q.is_amendment && (
                            <Badge className="border border-[#C4553B]/50 bg-[#C4553B]/10 text-[#F0C572]">
                              Avenant
                            </Badge>
                          )}
                          <span className="font-mono text-sm text-slate-100">
                            {q.number || "—"}
                          </span>
                          <Badge className={`border ${meta.cls}`}>{meta.label}</Badge>
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          {q.client_display} · {new Date(q.created_at).toLocaleDateString("fr-BE")}
                        </div>
                      </div>
                      <div className="tabular whitespace-nowrap text-right text-sm font-semibold text-slate-100">
                        {fmtEur(q.total_tvac)}
                      </div>
                    </Link>
                    {/* Turn Version chantier Lot A — bouton PDF sans prix par devis */}
                    <ExportExecutionBtn
                      quoteId={q.id}
                      isDraft={q.status === "brouillon"}
                    />
                  </li>
                );
                });
              })()}
            </ul>
          )}
        </CardContent>
      </Card>

      <ChantierWorkflowSection chantier={chantier} quotes={quotes} onChanged={load} currentUser={user} />

      <ChantierInvoicesSection chantier={chantier} quotes={quotes} onChanged={load} currentUser={user} />

      {/* Phase Rentab-1+2-B (13/09/2026) — Main-d'œuvre & sous-traitance
          Positionnée entre Workflow/Invoices et Coûts fournisseurs.
          Visibilité : masquée pour role=commercial (mirror RBAC backend). */}
      {!hasRole(user, ["commercial"]) && (
        <ChantierLaborAndSubcontractingCard
          chantier={chantier}
          onSaved={(updated) => setChantier((c) => ({ ...c, ...updated }))}
        />
      )}

      {/* Phase 10b — Section Coûts fournisseurs (liste + totaux) + Marge sur achats (Admin only)
          Lot Accès commercial — carte entière masquée pour role=commercial.
          Aucune donnée fournisseur (coûts détaillés, montants HTVA/TVAC) ne doit
          transiter vers les commerciaux, cf. `/chantiers/{id}/supplier-costs` désormais
          `require_admin_or_secretary` côté backend. */}
      {!hasRole(user, ["commercial"]) && (
        <Card
          className="border-slate-800 bg-slate-900/40"
          data-testid="chantier-supplier-costs-card"
        >
          <CardContent className="space-y-4 p-5">
            <h3 className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">
              <Wallet className="h-4 w-4 text-[#C4553B]" /> Coûts fournisseurs
            </h3>
            <ChantierSupplierCostsSection chantierId={chantier.id} />
            <ChantierMarginBlock chantierId={chantier.id} variant="full" />
          </CardContent>
        </Card>
      )}

      {/* Phase 9bis-hub-proactif — Prompt notify client au save date */}
      <Dialog
        open={!!notifyPrompt}
        onOpenChange={(o) => !o && !savingDate && setNotifyPrompt(null)}
      >
        <DialogContent
          className="border-slate-800 bg-slate-900"
          data-testid="chantier-start-date-notify-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-slate-100">
              {notifyPrompt?.isFirstSet ? "Fixer la date de démarrage" : "Modifier la date de démarrage"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {notifyPrompt?.newDate ? (
                <>
                  Nouvelle date :{" "}
                  <b className="text-slate-200">{fmtDateShortBE(notifyPrompt.newDate)}</b>
                  {notifyPrompt.oldDate && (
                    <span className="block text-xs text-slate-500">
                      Précédemment : {fmtDateShortBE(notifyPrompt.oldDate)}
                    </span>
                  )}
                </>
              ) : (
                "Retirer la date prévue"
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded border border-slate-800 bg-slate-950/30 p-3">
            <Checkbox
              id="notify-client-date"
              checked={notifyChecked}
              onCheckedChange={(v) => setNotifyChecked(!!v)}
              className="mt-0.5 border-slate-600 data-[state=checked]:bg-[#C4553B]"
              data-testid="chantier-start-date-notify-checkbox"
              disabled={!notifyPrompt?.newDate}
            />
            <div className="flex-1">
              <Label htmlFor="notify-client-date" className="cursor-pointer text-sm text-slate-200">
                Prévenir le client par email
              </Label>
              <p className="mt-0.5 text-xs text-slate-500">
                {notifyPrompt?.newDate
                  ? notifyPrompt?.isFirstSet
                    ? "Un email chaleureux informera le client de la date de démarrage."
                    : "Un email prévenant du changement de date sera envoyé."
                  : "Aucun email n'est envoyé lors du retrait d'une date."}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNotifyPrompt(null)}
              className="border-slate-700 bg-slate-800/60 text-slate-200"
              disabled={savingDate}
              data-testid="chantier-start-date-notify-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmDateSave}
              disabled={savingDate}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="chantier-start-date-notify-confirm-btn"
            >
              {savingDate ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
